import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { Button, Input, Text } from '../../components';
import { getFieldErrors } from '../AgentForm/utils/validateSilenceTriggers';
import type { SilenceTrigger } from '../../utilities';
import { Add } from '@mui/icons-material';

export type SilenceTriggersListProps = {
  value?: SilenceTrigger[];
  onChange?: (value: SilenceTrigger[]) => void;
  disabled?: boolean;
};

const EMPTY_TRIGGER: SilenceTrigger = { sec: 0, msg: '' };

export const SilenceTriggersList: React.FC<SilenceTriggersListProps> = ({
  value = [],
  onChange,
  disabled,
}) => {
  const addItem = () => onChange?.([...(value || []), { ...EMPTY_TRIGGER }]);

  const removeItem = (index: number) =>
    onChange?.(value.filter((_, i) => i !== index));

  const updateField = (
    index: number,
    field: keyof SilenceTrigger,
    newValue: string | number
  ) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange?.(updated);
  };

  return (
    <div className="w-full space-y-3 py-1">
      {value.map((item, i) => {
        const fieldErrors = getFieldErrors(item);
        const hasError = !!fieldErrors.sec || !!fieldErrors.msg;

        return (
          <div
            key={i}
            className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${
              hasError
                ? 'border-1 border-red-500 bg-red-50'
                : 'border border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex flex-col gap-3 flex-1">
              <div className="flex items-start gap-4">
                <Text
                  as="span"
                  variant="secondary"
                  className="text-small w-20 shrink-0 pt-2"
                >
                  Seconds
                </Text>
                <div className="flex flex-col gap-1">
                  <Input
                    numeric
                    min={1}
                    disabled={disabled}
                    value={item.sec || ''}
                    onChange={val =>
                      updateField(i, 'sec', parseInt(val, 10) || 0)
                    }
                    defaultSizeClass="w-28"
                  />
                  {!!fieldErrors.sec && (
                    <Text variant="error" className="text-small italic">
                      {fieldErrors.sec}
                    </Text>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Text
                  as="span"
                  variant="secondary"
                  className="text-small w-20 shrink-0 pt-2"
                >
                  Message
                </Text>
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <Input
                    multiline
                    size="small"
                    disabled={disabled}
                    value={item.msg}
                    onChange={val => updateField(i, 'msg', val)}
                    defaultSizeClass="w-full"
                  />
                  {!!fieldErrors.msg && (
                    <Text variant="error" className="text-small italic">
                      {fieldErrors.msg}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            {!disabled && (
              <Button
                type="empty"
                size="small"
                tooltip="Remove trigger"
                icon={<DeleteIcon fontSize="medium" />}
                onClick={() => removeItem(i)}
                className="text-danger hover:opacity-70 mt-1 shrink-0"
              />
            )}
          </div>
        );
      })}

      {!disabled && (
        <Button
          size="small"
          type="outline"
          icon={<Add fontSize="small" />}
          disabled={disabled}
          label="Add Trigger"
          onClick={addItem}
        />
      )}
    </div>
  );
};
